import { Button, Checkbox, FormControlLabel, MenuItem, TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fr } from 'date-fns/locale';
import { Field, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { addVisitToPatient, defaultPatientSummary, Medication, Visit } from '../../store/patientsSlice';
import { AppDispatch, RootState } from '../../store/store';
import InfoBox, { MedicationEdition, PageBox } from '../ui/DaliaComponents';

const newDiagnosticOptions = ['Rechute', 'Guérison', 'Récidive', 'Rémission', 'Nouveau diagnostic : dépression bipolaire'];

const VisitSchema = Yup.object().shape({
  date: Yup.date().required('Date de visite est requise'),
  newDiagnostic: Yup.string().required('Le diagnostic est requis'),
});

export const PatientInfo: React.FC = () => {
  const { patientEdited } = useSelector((state: RootState) => state.patients);
  const patient = patientEdited ? patientEdited : defaultPatientSummary;
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const initialValues = {
    date: new Date().toISOString(),
    medications: [] as Medication[],
    suicideAttempt: false,
    newDiagnostic: '',
  };
  if (patient.inclusionInfo?.medications) {
    initialValues.medications = patient.inclusionInfo.medications.map((medication: Medication) => ({
      ...medication,
    }));
  }
  const backToPatient = () => {
    navigate(`/patient/${patient.id}/view/visits`);
  };

  const addVisit = (visit: Visit) => {
    dispatch(addVisitToPatient({ id: patient.id, visit }));
    backToPatient();
  };

  return (
    <PageBox title={`Patient nº${patient.patientId} ${patientEdited?.externalId ? '- ' + patientEdited.externalId : ''} - Nouvelle Visite`}>
      <InfoBox emoji="⚠️">
        Vous êtes sur le point d’ajouter des informations liées à une nouvelle visite de votre patient, assurez vous que ces informations n’étaient pas vraies à
        l’inclusion. Dans le cas contraire, veuillez plutôt les ajouter dans les informations d'inclusion
      </InfoBox>
      <Formik initialValues={initialValues} onSubmit={addVisit} validationSchema={VisitSchema}>
        {({ errors, touched, handleSubmit, setFieldValue, values }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Grid alignItems="flex-start" container spacing={3}>
              <Grid offset={1} size={3}>
                Date de la visite
              </Grid>
              <Grid size={8}>
                <LocalizationProvider adapterLocale={fr} dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Date de la visite"
                    name="visitDate"
                    onChange={(newValue) => setFieldValue('date', newValue)}
                    slotProps={{
                      textField: {
                        error: touched.date && Boolean(errors.date),
                        helperText: touched.date && errors.date,
                        fullWidth: true,
                      },
                    }}
                    value={values.date !== '' ? new Date(values.date) : null}
                    views={['day', 'month', 'year']}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid offset={1} size={3}>
                Traitements médicamenteux en cours
              </Grid>
              <Grid size={8}>
                <MedicationEdition
                  medications={values.medications ?? []}
                  onMedicationsChange={(newMedications) => setFieldValue('medications', newMedications)}
                />
              </Grid>
              <Grid offset={1} size={3}>
                Évolution de la pathologie
              </Grid>
              <Grid size={8}>
                <Field
                  as={TextField}
                  error={touched.newDiagnostic && Boolean(errors.newDiagnostic)}
                  fullWidth
                  helperText={touched.newDiagnostic && errors.newDiagnostic}
                  label="Évolution de la pathologie"
                  name="newDiagnostic"
                  required
                  select
                >
                  {newDiagnosticOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Field>
                <Field
                  as={FormControlLabel}
                  control={<Checkbox checked={values.suicideAttempt} id="suicideAttempt" onChange={(e) => setFieldValue('suicideAttempt', e.target.checked)} />}
                  label="Tentative de suicide depuis la dernière visite"
                  name="suicideAttempt"
                ></Field>
              </Grid>

              <Grid container justifyContent="flex-end" size={12}>
                <Button onClick={backToPatient} variant="text">
                  Annuler
                </Button>
                <Button type="submit" variant="contained">
                  Valider
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </PageBox>
  );
};

export default PatientInfo;
